import React from 'react';
import { AutoSizer, Masonry, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import createMasonryCellPositioner from 'react-virtualized/dist/commonjs/Masonry/createCellPositioner';
import 'react-virtualized/styles.css';

const GAP = 8;
const DEFAULT_COLUMN_WIDTH = 260;
const DEFAULT_HEIGHT = 240;

const resolveColumnCount = (width) => {
  if (width >= 1600) {
    return 7;
  }
  if (width >= 1200) {
    return 5;
  }
  if (width >= 720) {
    return 3;
  }
  if (width >= 480) {
    return 2;
  }
  return 1;
};

const buildCellPositioner = (cache, width) => {
  const columnCount = resolveColumnCount(width);
  const adjustedColumnWidth = Math.max(160, Math.floor((width - GAP * (columnCount - 1)) / columnCount));
  return {
    columnCount,
    columnWidth: adjustedColumnWidth,
    positioner: createMasonryCellPositioner({
      cellMeasurerCache: cache,
      columnWidth: adjustedColumnWidth,
      spacer: GAP,
      columnCount,
    }),
  };
};

const GalleryMasonry = ({ images, renderItem }) => {
  const cacheRef = React.useRef(
    new CellMeasurerCache({
      defaultHeight: DEFAULT_HEIGHT,
      defaultWidth: DEFAULT_COLUMN_WIDTH,
      fixedWidth: true,
    }),
  );
  const positionerRef = React.useRef(null);
  const columnWidthRef = React.useRef(DEFAULT_COLUMN_WIDTH);
  const masonryRef = React.useRef(null);
  const windowHeightRef = React.useRef(typeof window !== 'undefined' ? window.innerHeight : 600);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }
    const handleResize = () => {
      windowHeightRef.current = window.innerHeight;
      masonryRef.current?.recomputeCellPositions();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    cacheRef.current.clearAll();
    positionerRef.current = null;
    masonryRef.current?.clearCellPositions();
  }, [images]);

  const cellRenderer = React.useCallback(
    ({ index, key, parent, style }) => {
      const image = images[index];
      if (!image) {
        return null;
      }
      return (
        <CellMeasurer cache={cacheRef.current} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          {({ registerChild }) => (
            <div ref={registerChild} style={style}>
              {renderItem(image, columnWidthRef.current)}
            </div>
          )}
        </CellMeasurer>
      );
    },
    [images, renderItem],
  );

  return (
    <div style={{ width: '100%', height: '100%', minHeight: '400px', overflowY: 'auto' }}>
      <AutoSizer>
        {({ width, height }) => {
          const effectiveWidth = Math.max(width, DEFAULT_COLUMN_WIDTH);
          const effectiveHeight = height && height > 0 ? height : Math.max(windowHeightRef.current - 160, 320);

          const { columnCount, columnWidth, positioner } = buildCellPositioner(cacheRef.current, effectiveWidth);
          if (positionerRef.current == null || columnWidthRef.current !== columnWidth) {
            positionerRef.current = positioner;
            columnWidthRef.current = columnWidth;
            masonryRef.current?.clearCellPositions();
          }

          return (
            <Masonry
              ref={masonryRef}
              cellCount={images.length}
              cellMeasurerCache={cacheRef.current}
              cellPositioner={positionerRef.current}
              cellRenderer={cellRenderer}
              height={effectiveHeight}
              width={effectiveWidth}
              columnWidth={columnWidth}
              overscanByColumns={Math.max(1, Math.floor(columnCount / 2))}
              autoHeight={false}
              overscanByPixels={300}
              style={{ outline: 'none' }}
            />
          );
        }}
      </AutoSizer>
    </div>
  );
};

export default GalleryMasonry;
